import type { DomainItem } from '@/types';
import i18n from '@/i18n/config';

import React from 'react';
import {
  RiDeleteBinLine,
  RiCheckboxCircleLine,
  RiTimeLine,
  RiErrorWarningLine,
  RiFileCopyLine,
  RiGlobalLine,
  RiAddLine,
  RiDownloadLine
} from '@remixicon/react';
import {
  Button,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Tooltip,
  useDisclosure
} from '@heroui/react';
import { useAtom, useAtomValue } from 'jotai';

import { TablePage, TableFilters, ColumnsToggleDropdown, FilterSelect } from '@/components/table';
import {
  domainsListParamsAtom,
  domainsQueryAtom,
  deleteDomain,
  updateDomainDebug,
  updateDomainTracking,
  setDomainExtra,
  verifyDomain,
  unverifyDomain
} from '@/store';
import { useTableExport } from '@/components/table/useTableExport';
import { ExportConfirmModal } from '@/components/table/ExportConfirmModal';
import { copyToClipboard } from '@/utils/helpers';

// Custom hooks
import { useDomainsFilters, useDomainsSorting } from '@/hooks/domains';
import { useVisibleColumns } from '@/hooks/table';

// Components
import { SetDnsModal } from '@/components/domains/SetDnsModal';
import { AddDomainModal } from '@/components/domains/AddDomainModal';
import { DomainStatusModal } from '@/components/domains/DomainStatusModal';
import { DomainExpandContent } from '@/components/domains/DomainExpandContent';
import { EditDomainModal } from '@/components/domains/EditDomainModal';
import { PermissionGuard } from '@/components/shared/PermissionGuard';
import { SearchInput } from '@/components/shared/SearchInput';
import { DateRangeFilter } from '@/components/shared/DateRangeFilter';
import { DomainCell } from '@/components/shared/DomainCell';
import { OrganizationCell } from '@/components/shared/OrganizationCell';
import { DateTimeDisplay } from '@/components/shared/DateTimeDisplay';

export default function DomainsPage() {
  const { data, isLoading, isError, error } = useAtomValue(domainsQueryAtom);
  const [params, setParams] = useAtom(domainsListParamsAtom);

  const domains = React.useMemo<DomainItem[]>(() => data?.data ?? [], [data]);

  // Custom hooks
  const {
    searchTerm,
    setSearchTerm,
    statusFilter,
    dateFilter,
    hasAnyFilter,
    handleSearch,
    handleClearSearch,
    clearFilters,
    handleStatusChange,
    handleDateChange,
    handlePageChange
  } = useDomainsFilters();

  const { sortDescriptor, setSortDescriptor, sortedDomains } = useDomainsSorting(domains);

  // Function to trigger data refresh
  const triggerRefresh = React.useCallback(() => {
    setParams((prev) => ({ ...prev, refresh: Date.now() }));
  }, [setParams]);

  // Copy unique_id to clipboard
  const copyUniqueId = React.useCallback(async (uniqueId: string, domainName: string) => {
    await copyToClipboard(uniqueId, `Unique ID for ${domainName} copied to clipboard`);
  }, []);

  // Toggle modals
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [deletingId, setDeletingId] = React.useState<number | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [toggleModal, setToggleModal] = React.useState<{
    id: number | null;
    type: 'debug' | 'tracking' | 'live_interaction' | 'verify' | 'unverify' | null;
    nextValue: boolean;
  } | null>(null);
  const { isOpen: isToggleOpen, onOpen: onToggleOpen, onOpenChange: onToggleOpenChange } = useDisclosure();

  // Status edit modal
  const [statusEditDomain, setStatusEditDomain] = React.useState<{ id: number; status: string } | null>(null);
  const { isOpen: isStatusEditOpen, onOpen: onStatusEditOpen, onOpenChange: onStatusEditOpenChange } = useDisclosure();

  // Set DNS modal
  const { isOpen: isSetDnsOpen, onOpen: onSetDnsOpen, onOpenChange: onSetDnsOpenChange } = useDisclosure();
  const [setDnsDomain, setSetDnsDomain] = React.useState<{ id: number; name: string } | null>(null);

  // Add Domain modal
  const { isOpen: isAddDomainOpen, onOpen: onAddDomainOpen, onOpenChange: onAddDomainOpenChange } = useDisclosure();

  // Edit Domain modal
  const [editDomain, setEditDomain] = React.useState<DomainItem | null>(null);
  const { isOpen: isEditDomainOpen, onOpen: onEditDomainOpen, onOpenChange: onEditDomainOpenChange } = useDisclosure();

  const requestDelete = React.useCallback(
    (id: number) => {
      setDeletingId(id);
      onOpen();
    },
    [onOpen]
  );

  const requestSetDns = React.useCallback(
    (id: number, name: string) => {
      setSetDnsDomain({ id, name });
      onSetDnsOpen();
    },
    [onSetDnsOpen]
  );

  const confirmDelete = React.useCallback(async () => {
    if (!deletingId) return;

    try {
      setIsDeleting(true);
      const response = await deleteDomain(deletingId);

      // Show success message
      if ((response as any)?.meta?.message) {
        const { toast } = await import('react-toastify');
        toast.success((response as any).meta.message);
      }

      // Trigger refresh to update UI
      triggerRefresh();

      // Close modal and reset state
      setIsDeleting(false);
      onOpenChange();
      setDeletingId(null);
    } catch (error) {
      setIsDeleting(false);
      // Error is handled by axios interceptor
    }
  }, [deletingId, onOpenChange, triggerRefresh]);

  const renderCell = React.useCallback(
    (item: DomainItem, columnKey: React.Key) => {
      switch (columnKey) {
        case 'domain': {
          return <DomainCell domain={item} showTitle />;
        }
        case 'id': {
          return <span className="text-default-600 font-mono text-xs">{item.id}</span>;
        }
        case 'status': {
          const isVerified = item.status === 'verified';
          const color = isVerified ? 'success' : 'warning';
          const label = isVerified ? 'Verified' : 'Pending';
          const Icon = isVerified ? RiCheckboxCircleLine : RiTimeLine;

          return (
            <Chip color={color as any} size="sm" variant="flat">
              <span className="flex items-center gap-1">
                <Icon className="h-4 w-4" /> {label}
              </span>
            </Chip>
          );
        }
        case 'token_limit':
          return item.token_limit != null ? (
            <span>{Number(item.token_limit).toLocaleString()}</span>
          ) : (
            <span className="text-default-500">Unlimited</span>
          );
        case 'organization':
          return item.organization ? (
            <OrganizationCell
              organization={{
                id: item.organization.id,
                name: item.organization.name,
                desc: null
              }}
              showDescription={false}
              dense
              linked
            />
          ) : (
            '-'
          );
        case 'unique_id':
          return (
            <Button
              className="bg-primary/10 hover:bg-primary/20 h-6 cursor-pointer px-2 transition-colors"
              color="primary"
              size="sm"
              variant="flat"
              onPress={() => copyUniqueId(item.unique_id, item.domain)}
              startContent={<RiFileCopyLine className="h-3 w-3" />}
            >
              <span className="font-mono text-xs">{item.unique_id}</span>
            </Button>
          );
        case 'created_at':
          return <DateTimeDisplay dateString={item.created_at} />;
        case 'actions':
          return (
            <div className="flex justify-end gap-1">
              <PermissionGuard permission="Domain.update">
                <Tooltip content={i18n.t('copy.set_dns')}>
                  <Button
                    isIconOnly
                    color={item.cdn || item.cdn_url || item.cdn_url_override ? 'primary' : 'danger'}
                    size="sm"
                    variant="light"
                    aria-label={i18n.t('copy.set_dns')}
                    onPress={() => requestSetDns(item.id, item.domain)}
                  >
                    <RiGlobalLine className="h-4 w-4" />
                  </Button>
                </Tooltip>
              </PermissionGuard>
              <PermissionGuard permission="Domain.destroy">
                <Tooltip color="danger" content={i18n.t('copy.delete_domain')}>
                  <Button
                    isIconOnly
                    color="danger"
                    size="sm"
                    variant="light"
                    aria-label={i18n.t('copy.delete_domain')}
                    onPress={() => requestDelete(item.id)}
                  >
                    <RiDeleteBinLine className="h-5 w-5" />
                  </Button>
                </Tooltip>
              </PermissionGuard>
            </div>
          );
        default:
          return null;
      }
    },
    [requestDelete, requestSetDns, onToggleOpen, copyUniqueId]
  );

  const columns = React.useMemo(
    () => [
      { name: i18n.t('columns.id'), uid: 'id', sortable: true, align: 'center', cellClass: 'text-center' },
      { name: i18n.t('columns.domain'), uid: 'domain', sortable: true },
      { name: i18n.t('filters.status'), uid: 'status', sortable: true },
      { name: i18n.t('copy.workspace'), uid: 'organization', sortable: true },
      { name: i18n.t('copy.domain_event_cap'), uid: 'token_limit', sortable: false },
      { name: i18n.t('copy.unique_id'), uid: 'unique_id', sortable: false },
      { name: i18n.t('columns.createdAt'), uid: 'created_at', sortable: true },
      { name: i18n.t('common.actions'), uid: 'actions', align: 'end', cellClass: 'text-right' }
    ],
    []
  );

  // Columns visibility management
  const { visibleColumns, setVisibleColumns, headerColumns } = useVisibleColumns(columns);

  const visibleColumnsSet = React.useMemo(
    () => new Set(Array.from(visibleColumns).map((c) => c.toString())),
    [visibleColumns]
  );

  const domainsMeta = data?.meta;

  const {
    exportState,
    isExporting,
    needsConfirmation,
    totalRecords,
    handleRequestExport,
    handleConfirmExport,
    handleCancelExport
  } = useTableExport<DomainItem>({
    resourceName: 'Domains',
    apiPath: '/domains',
    params: {
      page: params.page,
      per_page: params.per_page,
      filters: params.filters,
      sort: undefined
    },
    meta: domainsMeta
      ? {
          total: domainsMeta.total,
          per_page: domainsMeta.per_page,
          current_page: domainsMeta.current_page,
          last_page: domainsMeta.last_page
        }
      : undefined,
    columns: columns.map((c) => ({ name: c.name, uid: c.uid })),
    visibleColumns: visibleColumnsSet,
    hasAnyFilter: hasAnyFilter,
    mapItemToRow: (item) => ({
      id: item.id,
      domain: item.domain,
      status: item.status,
      organization: item.organization?.name ?? '',
      token_limit: item.token_limit != null ? String(item.token_limit) : 'Unlimited',
      unique_id: item.unique_id,
      created_at: item.created_at
    })
  });

  return (
    <TablePage
      columns={columns}
      currentPage={data?.meta?.current_page}
      data={sortedDomains}
      emptyMessage={i18n.t('copy.no_domains_found')}
      error={error}
      errorMessage={i18n.t('copy.failed_to_load_domains')}
      filtersSection={
        <TableFilters
          hasActiveFilters={hasAnyFilter}
          isRefreshing={isLoading}
          onClearFilters={clearFilters}
          onRefresh={triggerRefresh}
          rightSection={
            <Button
              color="success"
              size="sm"
              variant="flat"
              onPress={handleRequestExport}
              isLoading={isExporting}
              isDisabled={isLoading || !data?.meta?.total}
              startContent={<RiDownloadLine className="h-4 w-4" />}
            >{i18n.t('table.exportCsv')}</Button>
          }
        >
          <SearchInput
            placeholder={i18n.t('copy.search_domains')}
            value={searchTerm}
            onValueChange={setSearchTerm}
            onSearch={handleSearch}
            onClear={handleClearSearch}
          />

          <ColumnsToggleDropdown
            allColumns={columns.map((c) => ({ uid: c.uid, name: c.name }))}
            selected={visibleColumns}
            onChange={(next) => setVisibleColumns(next)}
          />

          <FilterSelect
            label={i18n.t('filters.status')}
            value={statusFilter}
            options={[
              { key: 'all', label: i18n.t('common.all') },
              { key: 'verified', label: i18n.t('filters.verified') },
              { key: 'pending', label: i18n.t('enums.pending') }
            ]}
            onChange={handleStatusChange}
          />

          <DateRangeFilter label={i18n.t('filters.createdDate')} value={dateFilter} onChange={handleDateChange} />
        </TableFilters>
      }
      headerColumns={headerColumns}
      isError={isError}
      isLoading={isLoading}
      renderCell={renderCell}
      sortDescriptor={sortDescriptor}
      title={i18n.t('nav.domains')}
      titleActions={
        <Button
          color="success"
          size="sm"
          variant="flat"
          startContent={<RiAddLine className="h-4 w-4" />}
          onPress={onAddDomainOpen}
        >
          Add Domain
        </Button>
      }
      totalPages={data?.meta?.last_page}
      onPageChange={handlePageChange}
      onRefresh={triggerRefresh}
      onSortChange={(d) => setSortDescriptor(d)}
      enableCollapse
      renderCollapseContent={(item) => (
        <DomainExpandContent
          domain={item}
          onToggleDebug={(checked) => {
            setToggleModal({ id: item.id, type: 'debug', nextValue: checked });
            onToggleOpen();
          }}
          onToggleTracking={(checked) => {
            setToggleModal({ id: item.id, type: 'tracking', nextValue: checked });
            onToggleOpen();
          }}
          onToggleLiveInteraction={(checked) => {
            setToggleModal({ id: item.id, type: 'live_interaction', nextValue: checked });
            onToggleOpen();
          }}
          onEditStatus={() => {
            setStatusEditDomain({ id: item.id, status: item.status });
            onStatusEditOpen();
          }}
          onEditCdn={() => {
            requestSetDns(item.id, item.domain);
          }}
          onEditDomain={() => {
            setEditDomain(item);
            onEditDomainOpen();
          }}
        />
      )}
    >
      {needsConfirmation && (
        <ExportConfirmModal
          isOpen={exportState === 'confirm'}
          onConfirm={handleConfirmExport}
          onCancel={handleCancelExport}
          totalRecords={totalRecords}
          resourceLabel="domain"
        />
      )}
      {/* Delete modal */}
      <Modal isOpen={isOpen} placement="center" onOpenChange={onOpenChange}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">{i18n.t('copy.delete_domain')}</ModalHeader>
              <ModalBody>
                <p>Are you sure you want to delete this domain?</p>
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose}>{i18n.t('modals.exportConfirm.cancel')}</Button>
                <Button color="danger" isLoading={isDeleting} onPress={confirmDelete}>{i18n.t('common.delete')}</Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Toggle confirm modal */}
      <Modal isOpen={isToggleOpen} placement="center" onOpenChange={onToggleOpenChange}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex items-center gap-2">
                <RiErrorWarningLine className="text-warning h-5 w-5" />
                <span>Confirm action</span>
              </ModalHeader>
              <ModalBody>
                <p className="leading-7">
                  Are you sure you want to
                  {toggleModal?.type === 'verify' || toggleModal?.type === 'unverify' ? (
                    <>
                      {' '}
                      <strong>{toggleModal?.type === 'verify' ? 'verify' : 'un-verify'}</strong> this domain?
                    </>
                  ) : (
                    <>
                      {' '}
                      <strong>{toggleModal?.nextValue ? 'enable' : 'disable'}</strong>{' '}
                      <strong>{toggleModal?.type}</strong> for this domain?
                    </>
                  )}
                </p>
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose}>{i18n.t('modals.exportConfirm.cancel')}</Button>
                <Button
                  color={toggleModal?.type === 'verify' || (toggleModal?.nextValue ?? false) ? 'success' : 'danger'}
                  isLoading={isDeleting}
                  onPress={async () => {
                    if (!toggleModal?.id || !toggleModal?.type) {
                      return;
                    }

                    try {
                      setIsDeleting(true);

                      let response: any = null;

                      if (toggleModal.type === 'debug') {
                        response = await updateDomainDebug(toggleModal.id, toggleModal.nextValue);
                      } else if (toggleModal.type === 'tracking') {
                        response = await updateDomainTracking(toggleModal.id, toggleModal.nextValue);
                      } else if (toggleModal.type === 'live_interaction') {
                        const domain = domains.find((d) => d.id === toggleModal.id);
                        const extra = {
                          force_click_tracking: domain?.extra?.force_click_tracking ?? true,
                          capture_all_properties: domain?.extra?.capture_all_properties ?? false,
                          referrer_tracking: domain?.extra?.referrer_tracking ?? true,
                          ignore_bots: domain?.extra?.ignore_bots ?? true,
                          enable_live_interaction: toggleModal.nextValue,
                        };
                        response = await setDomainExtra(toggleModal.id, extra);
                      } else if (toggleModal.type === 'verify') {
                        response = await verifyDomain(toggleModal.id);
                      } else if (toggleModal.type === 'unverify') {
                        response = await unverifyDomain(toggleModal.id);
                      }

                      // Show success message
                      const { toast } = await import('react-toastify');
                      const domainName = domains.find((d) => d.id === toggleModal.id)?.domain || 'domain';

                      if (toggleModal.type === 'verify') {
                        toast.success(`${domainName} verified successfully`);
                      } else if (toggleModal.type === 'unverify') {
                        toast.info(`${domainName} unverified successfully`);
                      } else if (toggleModal.nextValue) {
                        toast.success(
                          `${domainName}: ${toggleModal.type.charAt(0).toUpperCase() + toggleModal.type.slice(1)} enabled successfully`
                        );
                      } else {
                        toast.info(
                          `${domainName}: ${toggleModal.type.charAt(0).toUpperCase() + toggleModal.type.slice(1)} disabled successfully`
                        );
                      }

                      // Update the specific domain in cache
                      if ((response as any)?.data) {
                        triggerRefresh();
                      }

                      // Close modal and reset state
                      setIsDeleting(false);
                      onToggleOpenChange();
                      setToggleModal(null);
                    } catch (error) {
                      setIsDeleting(false);
                      // Error is handled by axios interceptor
                    }
                  }}
                >{i18n.t('common.confirm')}</Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Set DNS Modal */}
      {setDnsDomain && (
        <SetDnsModal
          isOpen={isSetDnsOpen}
          onOpenChange={onSetDnsOpenChange}
          domainId={setDnsDomain.id}
          domainName={setDnsDomain.name}
          onSuccess={triggerRefresh}
        />
      )}

      {/* Add Domain Modal */}
      <AddDomainModal isOpen={isAddDomainOpen} onOpenChange={onAddDomainOpenChange} onSuccess={triggerRefresh} />

      {/* Edit Domain Modal */}
      {editDomain && (
        <EditDomainModal
          domain={editDomain}
          isOpen={isEditDomainOpen}
          onOpenChange={(open) => {
            if (!open) setEditDomain(null);
            onEditDomainOpenChange();
          }}
          onSuccess={() => {
            setEditDomain(null);
            triggerRefresh();
          }}
        />
      )}

      {/* Domain Status Edit Modal */}
      {statusEditDomain && (
        <DomainStatusModal
          isOpen={isStatusEditOpen}
          onOpenChange={onStatusEditOpenChange}
          currentStatus={statusEditDomain.status}
          domainId={statusEditDomain.id}
          onConfirm={async (newStatus) => {
            const isVerified = newStatus === 'verified';
            if (isVerified) {
              await verifyDomain(statusEditDomain.id);
            } else {
              await unverifyDomain(statusEditDomain.id);
            }
            triggerRefresh();
          }}
        />
      )}
    </TablePage>
  );
}
